import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Lock, Loader2, FileX } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublishedNotePage() {
  const { token } = useParams();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isProtected, setIsProtected] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/published/${token}`);
        if (res.data.is_protected) {
          setIsProtected(true);
          setNote({ title: res.data.title, author_name: res.data.author_name, created_at: res.data.created_at, updated_at: res.data.updated_at });
        } else {
          setNote(res.data);
        }
      } catch {
        setNotFound(true);
      }
      setLoading(false);
    })();
  }, [token]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setPasswordError('');
    try {
      const res = await axios.post(`${API}/api/published/${token}/verify`, { password });
      setNote(res.data);
      setIsProtected(false);
    } catch {
      setPasswordError('Incorrect password');
    }
    setVerifying(false);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-500" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-4">
        <div className="text-center">
          <FileX className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-neutral-300 mb-2">Page not found</h1>
          <p className="text-sm text-neutral-500">This page may have been unpublished or the link is invalid.</p>
        </div>
      </div>
    );
  }

  if (isProtected) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center px-4">
        <meta name="robots" content="noindex, nofollow" />
        <div className="w-full max-w-sm space-y-6 text-center">
          <div>
            <Lock className="w-10 h-10 text-amber-500/60 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-neutral-200">{note?.title || 'Protected Page'}</h1>
            <p className="text-sm text-neutral-500 mt-1">This page is password protected.</p>
          </div>
          <form onSubmit={handleVerify} className="space-y-3">
            <Input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-neutral-900 border-neutral-800 text-neutral-200 text-sm"
              data-testid="published-password-input"
              autoFocus
            />
            {passwordError && <p className="text-xs text-red-400">{passwordError}</p>}
            <Button type="submit" disabled={verifying || !password} className="w-full" data-testid="published-password-submit">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              View Page
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <meta name="robots" content="noindex, nofollow" />
      <article className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {/* Header */}
        <header className="mb-8 pb-6 border-b border-neutral-800/60">
          <h1 className="text-2xl sm:text-3xl font-bold text-neutral-100 leading-tight" data-testid="published-title">
            {note.title || 'Untitled'}
          </h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-xs text-neutral-500">
            {note.author_name && <span>by {note.author_name}</span>}
            {note.created_at && <span>{formatDate(note.created_at)}</span>}
            {note.updated_at && note.updated_at !== note.created_at && (
              <span>Updated {formatDate(note.updated_at)}</span>
            )}
          </div>
        </header>

        {/* Content — rendered HTML from TipTap, sanitized */}
        <div
          className="published-content prose prose-invert prose-sm max-w-none break-words overflow-hidden"
          style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
          data-testid="published-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.content || '') }}
        />

        {/* Footer branding */}
        <footer className="mt-12 pt-6 border-t border-neutral-800/60 text-center">
          <p className="text-xs text-neutral-600">
            Published with <span className="text-amber-600/70 font-medium">VaultNotes</span>
          </p>
        </footer>
      </article>
    </div>
  );
}
